/*
 * [AI] 2026-05-19: 商品カードを全画像カルーセル化（無限ループ対応）。
 * - PC: hover で矢印表示、クリックで前後切替、両端でループ
 * - SP: scroll-snap でスワイプ切替、両端でループ
 *
 * 無限ループはクローン方式。
 * - 末尾画像のクローンを先頭に、先頭画像のクローンを末尾に prepend / append
 * - 初期位置を実先頭（index 1）にセット
 * - スクロール完了後にクローン位置にいたら無アニメーションで実位置にジャンプ
 *
 * 矢印ボタンのクリックはカードリンクへの伝播を止めて遷移を抑止する。
 */

const SCROLL_END_DEBOUNCE_MS = 80
const HAS_SCROLLEND = typeof window !== 'undefined' && 'onscrollend' in window

class MghProductCard extends HTMLElement {
  connectedCallback() {
    this.slides = this.querySelector('[data-mgh-pc-slides]')
    this.prevBtn = this.querySelector('[data-mgh-pc-prev]')
    this.nextBtn = this.querySelector('[data-mgh-pc-next]')
    if (!this.slides) return

    const realSlides = Array.from(this.slides.querySelectorAll('[data-mgh-pc-slide]'))
    this.totalReal = realSlides.length

    if (this.totalReal > 1) {
      this.setupLoop(realSlides)
    }

    this._onPrev = (event) => this.handleNav(event, -1)
    this._onNext = (event) => this.handleNav(event, 1)
    if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev)
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext)
  }

  disconnectedCallback() {
    if (this.prevBtn && this._onPrev) this.prevBtn.removeEventListener('click', this._onPrev)
    if (this.nextBtn && this._onNext) this.nextBtn.removeEventListener('click', this._onNext)
    if (this.slides) {
      if (this._onScroll) this.slides.removeEventListener('scroll', this._onScroll)
      if (this._onScrollEnd) this.slides.removeEventListener('scrollend', this._onScrollEnd)
    }
    if (this._scrollEndTimer) clearTimeout(this._scrollEndTimer)
    if (this._initObserver) {
      this._initObserver.disconnect()
      this._initObserver = null
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
  }

  setupLoop(realSlides) {
    // 末尾の実画像のクローンを先頭に、先頭の実画像のクローンを末尾に
    const firstClone = realSlides[0].cloneNode(true)
    const lastClone = realSlides[realSlides.length - 1].cloneNode(true)
    firstClone.dataset.mghPcClone = 'first'
    lastClone.dataset.mghPcClone = 'last'
    firstClone.setAttribute('aria-hidden', 'true')
    lastClone.setAttribute('aria-hidden', 'true')
    this.slides.appendChild(firstClone)
    this.slides.insertBefore(lastClone, this.slides.firstChild)

    // 初期位置: 実先頭（DOM 上は index 1）。
    // [AI] 2026-05-19: カードが display:none 配下にあると clientWidth が 0 のままになり
    // 旧実装では requestAnimationFrame の無限ループに陥っていた。最大 60 フレーム
    // （約 1 秒）でリトライを打ち切る + IntersectionObserver で可視化を待つ。
    let attempts = 0
    const MAX_ATTEMPTS = 60
    const positionInitial = () => {
      const width = this.slides.clientWidth
      if (width > 0) {
        this.slides.scrollLeft = width
        this._lastWidth = width
        return
      }
      attempts += 1
      if (attempts >= MAX_ATTEMPTS) {
        // 可視化されるまで IntersectionObserver で待機
        if (typeof IntersectionObserver === 'function' && !this._initObserver) {
          this._initObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && this.slides.clientWidth > 0) {
                this.slides.scrollLeft = this.slides.clientWidth
                this._lastWidth = this.slides.clientWidth
                this._initObserver.disconnect()
                this._initObserver = null
                break
              }
            }
          })
          this._initObserver.observe(this)
        }
        return
      }
      requestAnimationFrame(positionInitial)
    }
    positionInitial()

    // [AI] 2026-05-25: 画面拡大率変更 / ウィンドウリサイズで slide 幅が変わると、
    // 古い scrollLeft が新 clientWidth と合わなくなり 2 枚目以降が見切れる事象を修正。
    // ResizeObserver で幅変化を検知し、現在表示中の slide index を維持して scrollLeft を再計算。
    if (typeof ResizeObserver === 'function') {
      this._resizeObserver = new ResizeObserver(() => {
        const newWidth = this.slides.clientWidth
        if (newWidth <= 0) return
        const prevWidth = this._lastWidth || newWidth
        if (newWidth === prevWidth) return
        // 現在表示中の slide index を旧幅基準で算出 → 新幅で同じ index に再配置
        const currentIndex = Math.round(this.slides.scrollLeft / prevWidth)
        this.jumpTo(newWidth * currentIndex)
        this._lastWidth = newWidth
      })
      this._resizeObserver.observe(this.slides)
    }

    // スクロール終了検知。クローン位置にいたら実位置に無アニメーション転送。
    // iOS Safari は 'onscrollend' プロパティを持っていてもイベントが scroll-snap
    // との組み合わせで安定発火しないケースがあるため、scrollend と scroll+debounce
    // を併用する。handleScrollEnd は実位置では no-op なので多重発火は問題なし。
    this._onScroll = () => {
      if (this._scrollEndTimer) clearTimeout(this._scrollEndTimer)
      this._scrollEndTimer = setTimeout(() => this.handleScrollEnd(), SCROLL_END_DEBOUNCE_MS)
    }
    this.slides.addEventListener('scroll', this._onScroll, { passive: true })

    if (HAS_SCROLLEND) {
      this._onScrollEnd = () => this.handleScrollEnd()
      this.slides.addEventListener('scrollend', this._onScrollEnd, { passive: true })
    }
  }

  handleScrollEnd() {
    if (this.totalReal <= 1) return
    const width = this.slides.clientWidth
    if (width <= 0) return
    const index = Math.round(this.slides.scrollLeft / width)

    if (index === 0) {
      // 先頭クローン（実末尾の複製）→ 実末尾へジャンプ
      this.jumpTo(width * this.totalReal)
    } else if (index === this.totalReal + 1) {
      // 末尾クローン（実先頭の複製）→ 実先頭へジャンプ
      this.jumpTo(width)
    }
  }

  jumpTo(scrollLeft) {
    // scroll-snap 効いた状態で scrollLeft を瞬時に書き換えると一瞬の白飛びが出るブラウザがあるため、
    // 一時的に scroll-snap を無効化してから戻す。
    // [AI] 2026-05-19: rAF を 2 重にして、scrollLeft 反映と scroll-snap 再有効化を
    // 別フレームに分ける。iOS Safari の 1 フレーム競合（ガタつき）を防ぐ。
    const prevSnap = this.slides.style.scrollSnapType
    this.slides.style.scrollSnapType = 'none'
    this.slides.style.scrollBehavior = 'auto'
    this.slides.scrollLeft = scrollLeft
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.slides.style.scrollSnapType = prevSnap
        this.slides.style.scrollBehavior = ''
      })
    })
  }

  handleNav(event, direction) {
    event.preventDefault()
    event.stopPropagation()
    const width = this.slides.clientWidth
    if (width <= 0) return
    this.slides.scrollBy({ left: width * direction, behavior: 'smooth' })
  }
}

if (!customElements.get('mgh-product-card')) {
  customElements.define('mgh-product-card', MghProductCard)
}
